import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Terminal } from 'lucide-react';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SearchInput } from '@/components/ui/search-input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AssetCommandsDialog } from '@/features/assets/components/AssetCommandsDialog';
import {
  DevicePlatformIcon,
  SessionStateBadge,
} from '@/features/assets/components/DeviceBadges';
import { DesktopEnrollmentKeyCard } from '@/features/assets/components/DesktopEnrollmentKeyCard';
import {
  canListRemoteDevices,
  COMMANDS_PERMISSION,
  RemoteActionsMenu,
} from '@/features/assets/components/RemoteActionsMenu';
import {
  assetRemoteService,
  RemoteChannel,
  RemoteDevice,
} from '@/features/assets/services/asset-remote.service';

export const Route = createFileRoute('/_authenticated/remote-commands')({
  component: RemoteCommandsPage,
});

const CHANNEL: Record<RemoteChannel, { label: string; className: string; order: number }> = {
  websocket: { label: 'Online', className: 'bg-green-600', order: 0 },
  polling: { label: 'Online (sem WebSocket)', className: 'bg-amber-500', order: 1 },
  offline: { label: 'Offline', className: 'bg-gray-500', order: 2 },
};

function RemoteCommandsPage() {
  const { hasPermission } = usePermissions();
  // A lista vale para assets.commands OU assets.remote_control; o terminal só com commands
  const canList = canListRemoteDevices(hasPermission);
  const canRunCommands = hasPermission(COMMANDS_PERMISSION);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<RemoteDevice | null>(null);

  const { data: devices = [], isLoading } = useQuery({
    queryKey: ['remote-devices'],
    queryFn: () => assetRemoteService.listDevices(),
    refetchInterval: 15_000,
    enabled: canList,
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return devices
      .filter((d) =>
        !term
          ? true
          : [d.assetName, d.assetCode, d.hostname, d.location, d.macAddress]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
      )
      .sort((a, b) => CHANNEL[a.channel].order - CHANNEL[b.channel].order);
  }, [devices, search]);

  const onlineCount = devices.filter((d) => d.channel !== 'offline').length;

  if (!canList) return null;

  return (
    <>
      <div className="container mx-auto space-y-6 py-6">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <Terminal className="h-7 w-7" />
            Comandos Remotos
          </h1>
          <p className="text-muted-foreground">
            Envie comandos (PowerShell/CMD no Windows, shell no macOS) e ações remotas aos PCs com
            o Asseto Desktop e veja as respostas.
          </p>
        </div>

        <DesktopEnrollmentKeyCard />

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
            <CardTitle className="text-base">
              {onlineCount} de {devices.length} notebooks online
            </CardTitle>
            <div className="w-72">
              <SearchInput
                onSearch={setSearch}
                placeholder="Buscar por nome, código, hostname..."
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Carregando...</p>
            ) : devices.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhum notebook com o Asseto Desktop registrado ainda. O registro acontece
                automaticamente no primeiro login online depois da atualização do app.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Equipamento</TableHead>
                    <TableHead>Hostname</TableHead>
                    <TableHead>Sessão</TableHead>
                    <TableHead>Versão</TableHead>
                    <TableHead>Visto por último</TableHead>
                    <TableHead className="text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((device) => (
                    <TableRow key={device.deviceId}>
                      <TableCell>
                        <Badge className={`${CHANNEL[device.channel].className} text-white`}>
                          {CHANNEL[device.channel].label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {device.assetId ? (
                          <>
                            <div className="font-medium">{device.assetName}</div>
                            <div className="text-xs text-muted-foreground">
                              {[device.assetCode, device.location].filter(Boolean).join(' · ')}
                            </div>
                          </>
                        ) : (
                          <Badge variant="outline" className="border-amber-500 text-amber-600">
                            Não cadastrado
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <DevicePlatformIcon platform={device.platform} />
                          {device.hostname || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <SessionStateBadge state={device.sessionState} userEmail={device.userEmail} />
                        {!device.sessionState && '-'}
                      </TableCell>
                      <TableCell>{device.appVersion ? `v${device.appVersion}` : '-'}</TableCell>
                      <TableCell>
                        {device.lastSeenAt
                          ? formatDistanceToNow(new Date(device.lastSeenAt), {
                              addSuffix: true,
                              locale: ptBR,
                            })
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <RemoteActionsMenu
                            deviceId={device.deviceId}
                            deviceName={device.assetName || device.hostname || 'PC'}
                            online={device.online ?? device.channel !== 'offline'}
                          />
                          {/* Terminal precisa de equipamento: cadastre o PC pelo Realtime */}
                          {canRunCommands && device.assetId && (
                            <Button size="sm" onClick={() => setSelected(device)}>
                              <Terminal className="mr-2 h-4 w-4" />
                              Abrir terminal
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <AssetCommandsDialog
          asset={
            selected?.assetId
              ? {
                  id: selected.assetId,
                  name: selected.assetName ?? selected.hostname ?? 'PC',
                  platform: selected.platform,
                }
              : null
          }
          open={!!selected}
          onOpenChange={(open) => !open && setSelected(null)}
        />
      </div>
    </>
  );
}
